
// frontend/src/components/Sports/SportsInjector.js
// Injects live scoreboard into sports-related articles based on tags
import React from 'react';
import { motion } from 'framer-motion';
import { ScoreboardGrid } from './ScoreboardGrid';
import { Link } from 'react-router-dom';
import { Trophy, ChevronRight } from 'lucide-react';

// Map article tags to league configurations
const TAG_TO_LEAGUE = {
    'Premier League': { sport: 'soccer', league: 'eng.1', name: 'Premier League', path: '/sports/soccer/premier-league' },
    'EPL': { sport: 'soccer', league: 'eng.1', name: 'Premier League', path: '/sports/soccer/premier-league' },
    'La Liga': { sport: 'soccer', league: 'esp.1', name: 'La Liga', path: '/sports/soccer/la-liga' },
    'Bundesliga': { sport: 'soccer', league: 'ger.1', name: 'Bundesliga', path: '/sports/soccer/bundesliga' },
    'Serie A': { sport: 'soccer', league: 'ita.1', name: 'Serie A', path: '/sports/soccer/serie-a' },
    'Champions League': { sport: 'soccer', league: 'uefa.champions', name: 'Champions League', path: '/sports/soccer/champions-league' },
    'UCL': { sport: 'soccer', league: 'uefa.champions', name: 'Champions League', path: '/sports/soccer/champions-league' },
    'NBA': { sport: 'basketball', league: 'nba', name: 'NBA', path: '/sports/basketball/nba' },
    'NFL': { sport: 'football', league: 'nfl', name: 'NFL', path: '/sports/football/nfl' },
    'F1': { sport: 'racing', league: 'f1', name: 'Formula 1', path: '/sports/racing/f1' },
    'Formula 1': { sport: 'racing', league: 'f1', name: 'Formula 1', path: '/sports/racing/f1' },
    'Sports': { sport: 'soccer', league: 'eng.1', name: 'Premier League', path: '/sports' }, // Default for generic sports tag
    'Soccer': { sport: 'soccer', league: 'eng.1', name: 'Premier League', path: '/sports' },
    'Football': { sport: 'football', league: 'nfl', name: 'NFL', path: '/sports/football/nfl' }
};

/**
 * SportsInjector - Detects sports-related tags and injects live scoreboard
 * @param {Object} article - The article object with tags array
 */
export const SportsInjector = ({ article }) => {
    if (!article?.tags || !Array.isArray(article.tags)) {
        return null;
    }

    // Find first matching sports tag
    let matchedLeague = null;
    for (const tag of article.tags) {
        if (TAG_TO_LEAGUE[tag]) {
            matchedLeague = TAG_TO_LEAGUE[tag];
            break;
        }
    }

    // No sports tags found
    if (!matchedLeague) {
        return null;
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
        >
            <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 to-transparent overflow-hidden">
                {/* Header */}
                <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Trophy className="w-4 h-4 text-primary" />
                        <span className="text-sm font-bold text-white">
                            Live {matchedLeague.name} Scores
                        </span>
                        <span className="px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded-full animate-pulse">
                            LIVE
                        </span>
                    </div>
                    <Link
                        to={matchedLeague.path}
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                    >
                        <span>View All</span>
                        <ChevronRight className="w-3 h-3" />
                    </Link>
                </div>

                {/* Scoreboard */}
                <div className="p-4">
                    <ScoreboardGrid
                        sport={matchedLeague.sport}
                        league={matchedLeague.league}
                        limit={3}
                    />
                </div>
            </div>
        </motion.div>
    );
};
